/**
 * Reusable switch/toggle component.
 * @param {boolean} checked - Current on/off state
 * @param {function(boolean)} onChange - Called with new value when toggled
 * @param {string} [label] - Optional label shown to the left of the switch
 * @param {boolean} [disabled] - Whether the switch is disabled
 * @param {string} [className] - Extra class names for the wrapper
 * @param {string} [labelClassName] - Extra class names for label text
 * @param {string} [trackClassName] - Extra class names for switch track
 * @param {string} [thumbClassName] - Extra class names for switch thumb
 */
export default function SwitchComponent({
    checked,
    onChange,
    label,
    disabled = false,
    className = "",
    labelClassName = "",
    trackClassName = "",
    thumbClassName = "",
}) {
    const handleClick = () => {
        if (!disabled && typeof onChange === "function") {
            onChange(!checked);
        }
    };

    return (
        <label
            className={`group flex items-center gap-2 cursor-pointer select-none ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
            onClick={disabled ? undefined : (e) => { e.preventDefault(); handleClick(); }}
        >
            {label != null && label !== "" && (
                <span className={`text-sm text-gray-600 transition-colors duration-200 ${checked ? "text-gray-800" : ""} ${labelClassName}`}>
                    {label}
                </span>
            )}
            <div
                role="switch"
                aria-checked={checked}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleClick();
                    }
                }}
                className={[
                    "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5",
                    "transition-[background-color,box-shadow,transform] duration-300 ease-in-out",
                    checked
                        ? "bg-emerald-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_2px_10px_rgba(16,185,129,0.4)]"
                        : "bg-gray-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.08)]",
                    !disabled && "hover:shadow-md active:scale-[0.96] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1",
                    trackClassName,
                ].join(" ")}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClick(); }}
            >
                <span
                    aria-hidden
                    className={[
                        "absolute inset-0 rounded-full transition-opacity duration-300",
                        checked ? "opacity-100 bg-gradient-to-b from-white/25 to-transparent" : "opacity-0",
                    ].join(" ")}
                />
                <span
                    className={[
                        "pointer-events-none relative z-[1] inline-block h-4 w-4 rounded-full bg-white",
                        "transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
                        "will-change-transform",
                        checked
                            ? "translate-x-6 shadow-[0_2px_8px_rgba(0,0,0,0.22)]"
                            : "translate-x-0 shadow-[0_1px_4px_rgba(0,0,0,0.18)]",
                        !disabled && "group-active:scale-90",
                        thumbClassName,
                    ].join(" ")}
                />
            </div>
        </label>
    );
}
